import { useState, useCallback, useRef, useEffect } from 'react';
import { useReso } from '../../../../../contexts/ResoContext';
import { useMetadata } from '../../../../../contexts/MetadataContext';
import {
  dataURItoBlob,
  downscaleImage,
  getExifInfo,
  ImageUtils,
} from '../../utils/imageUtils';
import {
  BatchProcessedImageData,
  ProcessedImageData,
} from '../../ImageEditDialog';
import { MediaResource } from '@brightmls/nextgen.lae.flc';
import { useService } from '../../../../../contexts/ServiceContext';
import {
  BatchReplaceResult,
  MEDIA_TIMEOUT,
  MediaMetadata,
  MediaUploadResult,
  MediaUploadState,
  UploadStage,
  UseMediaUploadOptions,
} from './types';
import {
  getBase64Data,
  getStageStatusText,
  isMediaResourceValid,
  retryWithExponentialBackoff,
} from './utils';
import {
  isMimeTypeNullError,
  translateMediaRecord,
} from '../../../../../system/media';

export function useMediaUpload(options: UseMediaUploadOptions) {
  const {
    listingKey,
    listingDetails,
    onUploadComplete,
    onBatchComplete,
    onUploadError,
    onReplaceComplete,
    onReplaceBatchComplete,
    onReplaceError,
    pollInterval = 500,
    maxPollAttempts = 120,
    imageConfig = {
      maxWidth: ImageUtils.TARGET_WIDTH,
      maxHeight: ImageUtils.TARGET_HEIGHT,
      quality: ImageUtils.JPEG_COMPRESSION,
    },
    maxShadowFailures = 5,
    logger,
    onMimeTypeError,
  } = options;

  // Enhanced upload progress state
  const [uploadProgress, setUploadProgress] = useState({
    totalFiles: 0,
    completedFiles: 0,
    currentFile: {
      name: '',
      previewUrl: '',
      stage: 'preparing' as UploadStage,
      progress: 0,
      statusText: '',
    },
    remainingFiles: [] as string[],
    failedFiles: [] as { name: string; error: string }[],
  });

  // Track media items being polled - using ref instead of state for immediate access
  const pendingMediaPollsRef = useRef<
    Record<
      string,
      {
        mediaKey: string;
        metadata: MediaMetadata;
        startTime: number;
        mediaResource?: MediaResource;
      }
    >
  >({});

  // Track if batch polling is active
  const [isBatchPollingActive, setIsBatchPollingActive] = useState(false);

  // Batch polling interval reference
  const batchPollingIntervalRef = useRef<number | null>(null);

  // Logger reference for consistent logging
  const loggerRef = useRef<any>(null);

  // Refs to store progress simulation intervals
  const progressIntervals = useRef<Record<string, number>>({});

  // Track shadow failure counts per media item
  const shadowFailureCountsRef = useRef<Record<string, number>>({});

  // Track current operation type for appropriate callback selection
  const currentOperationTypeRef = useRef<'upload' | 'replace' | null>(null);

  const { executeQuery } = useReso();
  const { locale } = useMetadata();
  const [activeUploads, setActiveUploads] = useState(new Set<string>());
  const {
    isRequestInProgress,
    getCurrentRequestPromise,
    registerRequestOperation,
  } = useService();
  const [isAnyUploading, setIsAnyUploading] = useState(false);
  const [uploadStates, setUploadStates] = useState<
    Record<string, MediaUploadState>
  >({});
  const [pendingMediaPolls, setPendingMediaPolls] = useState<
    Record<
      string,
      {
        mediaKey: string;
        metadata: MediaMetadata;
        startTime: number;
      }
    >
  >({});

  const translation = locale();

  /**
   * Create media record with 30-second timeout
   */
  const createMediaRecord = useCallback(
    async (payload: any): Promise<any> => {
      try {
        return await executeQuery<any>('CWA_Media', {
          method: 'POST',
          body: payload,
          timeout: MEDIA_TIMEOUT,
        });
      } catch (error) {
        console.error('createMediaRecord failed:', error);
        if (isMimeTypeNullError(error)) {
          onMimeTypeError?.(error as Error, 'create');
        }
        throw error;
      }
    },
    [executeQuery, onMimeTypeError],
  );

  /**
   * Upload media content with 30-second timeout
   */
  const uploadMediaContent = useCallback(
    async (mediaKey: string, blob: Blob, contentType: string): Promise<any> => {
      try {
        return await executeQuery(`CWA_Media('${mediaKey}')/$value`, {
          method: 'PUT',
          body: blob,
          headers: {
            'Content-Type': contentType,
          },
          buffer: true,
          timeout: MEDIA_TIMEOUT,
        });
      } catch (error) {
        console.error('uploadMediaContent failed:', error);
        if (isMimeTypeNullError(error)) {
          onMimeTypeError?.(error as Error, 'upload');
        }
        throw error;
      }
    },
    [executeQuery, onMimeTypeError],
  );

  /**
   * Replace media content with 30-second timeout
   */
  const replaceMediaContent = useCallback(
    async (
      mediaKey: string,
      blob: Blob | null,
      contentType: string | undefined,
    ): Promise<any> => {
      try {
        return await executeQuery(`CWA_Media('${mediaKey}')/$value`, {
          method: 'PUT',
          body: blob,
          headers: {
            'Content-Type': contentType,
          },
          buffer: true,
          timeout: MEDIA_TIMEOUT,
        });
      } catch (error) {
        console.error('replaceMediaContent failed:', error);
        if (isMimeTypeNullError(error)) {
          onMimeTypeError?.(error as Error, 'replace');
        }
        throw error;
      }
    },
    [executeQuery, onMimeTypeError],
  );

  /**
   * Helper function to wait for any active request operations to complete
   */
  const waitForRequestCompletion = useCallback(
    async (
      operationName: string,
      mediaItems: MediaMetadata[] | ProcessedImageData[],
    ): Promise<void> => {
      if (isRequestInProgress()) {
        setUploadProgress({
          totalFiles: mediaItems.length,
          completedFiles: 0,
          currentFile: {
            name: mediaItems[0]?.file?.name || '',
            previewUrl: '',
            stage: 'waiting',
            progress: 0,
            statusText: 'Waiting for other operations to complete...',
          },
          remainingFiles: mediaItems
            .slice(1)
            .map((item) => item.file?.name || ''),
          failedFiles: [],
        });

        const requestPromise = getCurrentRequestPromise();

        if (requestPromise) {
          const log = loggerRef.current?.log || console.log;
          log(
            `Waiting for request operation to complete before ${operationName}`,
          );

          try {
            await requestPromise;
            log(
              `Request operation completed, proceeding with ${operationName}`,
            );
          } catch (requestError) {
            // Log the request error but continue with operation
            log(
              `Request operation failed, but continuing with ${operationName}:`,
              requestError,
            );
          }
        }
      }
    },
    [isRequestInProgress, getCurrentRequestPromise],
  );

  /**
   * Clear all progress simulation intervals to prevent memory leaks
   */
  const clearAllIntervals = () => {
    Object.values(progressIntervals.current).forEach((interval) => {
      clearInterval(interval);
    });
    progressIntervals.current = {};

    // Also clear batch polling interval
    if (batchPollingIntervalRef.current) {
      clearInterval(batchPollingIntervalRef.current);
      batchPollingIntervalRef.current = null;
    }

    // Clear shadow failure counts when clearing all intervals
    shadowFailureCountsRef.current = {};
  };

  /**
   * Start a progress simulation interval for the current file
   */
  const startProgressSimulation = (
    fileName: string,
    fromStage: UploadStage,
    fromProgress: number,
    toProgress: number,
    duration: number = 10000,
  ) => {
    // Clear any existing interval for this file
    if (progressIntervals.current[fileName]) {
      clearInterval(progressIntervals.current[fileName]);
    }

    const steps = Math.max(10, Math.floor(duration / 100)); // At least 10 steps, aim for ~100ms per step
    const progressPerStep = (toProgress - fromProgress) / steps;
    let currentStep = 0;

    progressIntervals.current[fileName] = window.setInterval(
      () => {
        setUploadProgress((prev) => {
          // Don't update if we've moved to a different stage or file
          if (
            prev.currentFile.name !== fileName ||
            prev.currentFile.stage !== fromStage
          ) {
            clearInterval(progressIntervals.current[fileName]);
            delete progressIntervals.current[fileName];
            return prev;
          }

          currentStep++;
          const newProgress = Math.min(
            fromProgress + currentStep * progressPerStep,
            toProgress,
          );

          // If we've reached the target progress, clear the interval
          if (currentStep >= steps || newProgress >= toProgress) {
            clearInterval(progressIntervals.current[fileName]);
            delete progressIntervals.current[fileName];
          }

          return {
            ...prev,
            currentFile: {
              ...prev.currentFile,
              progress: newProgress,
              statusText: `${getStageStatusText(fromStage)} (${Math.round(newProgress)}%)`,
            },
          };
        });
      },
      Math.floor(duration / steps),
    );
  };

  /**
   * Handles batch polling for media processing status
   */
  const startBatchPolling = useCallback(() => {
    const log = loggerRef.current?.log || console.log;
    const error = loggerRef.current?.error || console.error;

    // Prevent multiple intervals being created
    if (batchPollingIntervalRef.current) {
      log('Clearing existing batch polling interval');
      clearInterval(batchPollingIntervalRef.current);
      batchPollingIntervalRef.current = null;
    }

    // Mark as active immediately
    setIsBatchPollingActive(true);

    // Create a robust polling function
    const doBatchPoll = async () => {
      try {
        const pendingItems = pendingMediaPollsRef.current;
        const pendingCount = Object.keys(pendingItems).length;

        // Exit if no items to process
        if (pendingCount === 0) {
          log('No pending media items, stopping batch polling');
          setIsBatchPollingActive(false);
          // Also set isAnyUploading to false when we have no pending items
          setIsAnyUploading(false);

          if (currentOperationTypeRef.current === 'replace') {
            onReplaceBatchComplete?.();
          } else {
            onBatchComplete?.();
          }

          // Reset operation type
          currentOperationTypeRef.current = null;

          if (batchPollingIntervalRef.current) {
            clearInterval(batchPollingIntervalRef.current);
            batchPollingIntervalRef.current = null;
          }
          return;
        }

        log(`Running batch poll for ${pendingCount} media items`);
        log(`Current pending items: ${Object.keys(pendingItems).join(', ')}`);

        // Fetch all media for this listing in one request
        const result = await executeQuery<any>('CWA_Media', {
          queryParams: {
            $filter: `CWAResourceRecordKey eq '${listingKey}'`,
            $format: 'json',
          },
        });

        log('CWA_Media Polling query result:', result);

        if (!result || !result.value || !Array.isArray(result.value)) {
          error('Invalid response from batch media query', result);
          return;
        }

        log(`Retrieved ${result.value.length} media items in batch query`);

        // Process each pending media item
        const currentTime = Date.now();
        const completedItems: string[] = [];

        for (const [fileName, pollInfo] of Object.entries(pendingItems)) {
          const { mediaKey, metadata, startTime } = pollInfo;

          // Find this media in the results
          const mediaItem = result.value.find(
            (item: MediaUploadResult) => item.CWAMEDVWKey === mediaKey,
          );

          // Check if this item has timed out
          const hasTimedOut =
            currentTime - startTime > maxPollAttempts * pollInterval;

          if (hasTimedOut) {
            log(
              `Media ${fileName} processing timed out after ${(currentTime - startTime) / 1000}s`,
            );

            // Handle timeout logic...
            // Clear progress simulation
            if (progressIntervals.current[fileName]) {
              clearInterval(progressIntervals.current[fileName]);
              delete progressIntervals.current[fileName];
            }

            // Update UI states
            setUploadProgress((prev) => ({
              ...prev,
              currentFile: {
                ...prev.currentFile,
                stage: 'error',
                error: 'Upload timed out waiting for processing',
                statusText: 'Upload timed out',
              },
              failedFiles: [
                ...prev.failedFiles,
                {
                  name: fileName,
                  error: 'Upload timed out waiting for processing',
                },
              ],
            }));

            setUploadStates((prev) => ({
              ...prev,
              [fileName]: {
                previewUrl: prev[fileName]?.previewUrl || '',
                isUploading: false,
                error: new Error('Upload timed out'),
                progress: 0,
              },
            }));

            setActiveUploads((prev) => {
              const next = new Set(prev);
              next.delete(fileName);
              return next;
            });

            // Call error handler
            onUploadError?.(new Error('Upload timed out'), metadata);
            completedItems.push(fileName);
            continue;
          }

          // Skip if not processed yet
          if (
            !mediaItem ||
            mediaItem.CWAMediaProcessingStatus !== 'PROCESSED'
          ) {
            log(
              `Media ${fileName} not yet processed, continuing to monitor...`,
            );
            continue;
          }

          // Media is processed! Validate and complete
          const translatedResult = translateMediaRecord(mediaItem, translation);

          // Initialize shadow failure count if not exists
          if (!shadowFailureCountsRef.current[fileName]) {
            shadowFailureCountsRef.current[fileName] = 0;
          }

          if (isMediaResourceValid(translatedResult)) {
            log(`Media ${fileName} successfully processed!`);

            // Clear progress simulation
            if (progressIntervals.current[fileName]) {
              clearInterval(progressIntervals.current[fileName]);
              delete progressIntervals.current[fileName];
            }

            // Update UI states
            setUploadProgress((prev) => {
              const nextInQueue = prev.remainingFiles[0];

              return {
                ...prev,
                completedFiles: prev.completedFiles + 1,
                currentFile: nextInQueue
                  ? {
                      name: nextInQueue,
                      previewUrl: '',
                      stage: 'preparing',
                      progress: 0,
                      statusText: 'Preparing...',
                    }
                  : {
                      ...prev.currentFile,
                      stage: 'complete',
                      progress: 100,
                      statusText: 'Upload complete!',
                    },
                remainingFiles: prev.remainingFiles.slice(1),
              };
            });

            setUploadStates((prev) => ({
              ...prev,
              [fileName]: {
                isUploading: false,
                progress: 100,
                previewUrl: prev[fileName]?.previewUrl || '',
              },
            }));

            setActiveUploads((prev) => {
              const next = new Set(prev);
              next.delete(fileName);
              return next;
            });

            if (currentOperationTypeRef.current === 'replace') {
              onReplaceComplete?.(translatedResult);
            } else {
              onUploadComplete?.(translatedResult);
            }
            completedItems.push(fileName);
          } else {
            // Increment shadow failure count
            shadowFailureCountsRef.current[fileName]++;

            // Lets emit this to the MEDIA debug log (this 'shadow' failure should never happen -- but it does)
            log(
              `Shadow failure increment for media ${fileName}, ${shadowFailureCountsRef.current[fileName]}/${maxShadowFailures}... Keys on MEDIA: ${JSON.stringify(
                Object.keys(translatedResult),
              )}`,
            );

            if (shadowFailureCountsRef.current[fileName] >= maxShadowFailures) {
              // Shadow failure handling
              log(
                `Shadow failure detected for media ${fileName}, cleaning up...`,
              );

              try {
                await executeQuery(`CWA_Media('${mediaKey}')`, {
                  method: 'DELETE',
                });
                log(`Cleaned up invalid media ${fileName}`);
              } catch (deleteError) {
                error('Failed to clean up invalid media:', deleteError);
              }

              // Update UI with error
              if (progressIntervals.current[fileName]) {
                clearInterval(progressIntervals.current[fileName]);
                delete progressIntervals.current[fileName];
              }

              setUploadProgress((prev) => ({
                ...prev,
                currentFile: {
                  ...prev.currentFile,
                  stage: 'error',
                  error: 'Upload succeeded but media is invalid',
                  statusText: 'Invalid media resource',
                },
                failedFiles: [
                  ...prev.failedFiles,
                  {
                    name: fileName,
                    error: 'Upload succeeded but media is invalid',
                  },
                ],
              }));

              setUploadStates((prev) => ({
                ...prev,
                [fileName]: {
                  isUploading: false,
                  progress: 0,
                  error: new Error('Media validation failed'),
                  previewUrl: prev[fileName]?.previewUrl || '',
                },
              }));

              setActiveUploads((prev) => {
                const next = new Set(prev);
                next.delete(fileName);
                return next;
              });

              onUploadError?.(new Error('Media validation failed'), metadata);
              completedItems.push(fileName);
            } else {
              // Continue polling if not reached max attempts
              log(
                `Continuing to poll for media (attempt ${shadowFailureCountsRef.current[fileName]} of ${maxShadowFailures})`,
              );
            }
          }
        }

        // Process completed items
        if (completedItems.length > 0) {
          log(
            `Completed ${completedItems.length} media items in this batch poll`,
          );

          // Remove completed items from pending
          completedItems.forEach((key) => {
            delete pendingMediaPollsRef.current[key];

            // Also clean up shadow failure counts for completed items
            delete shadowFailureCountsRef.current[key];
          });

          // Check if all items are done
          const remainingCount = Object.keys(
            pendingMediaPollsRef.current,
          ).length;
          if (remainingCount === 0) {
            log('All media items processed, stopping batch polling');
            setIsBatchPollingActive(false);
            // Update isAnyUploading when all items are processed
            setIsAnyUploading(false);
            if (currentOperationTypeRef.current === 'replace') {
              onReplaceBatchComplete?.();
            } else {
              onBatchComplete?.();
            }

            currentOperationTypeRef.current = null;

            if (batchPollingIntervalRef.current) {
              clearInterval(batchPollingIntervalRef.current);
              batchPollingIntervalRef.current = null;
            }
          } else {
            log(`Still monitoring ${remainingCount} pending items`);
          }
        }
      } catch (err) {
        // Handle errors but continue polling
        const errMsg = err instanceof Error ? err.message : String(err);
        error('Error in batch polling:', errMsg);
      }
    };

    // Execute the first poll immediately
    log('Executing first batch poll');
    doBatchPoll();

    // Set up interval for subsequent polls - this is the critical part!
    log(`Setting up batch polling interval (${pollInterval}ms)`);
    batchPollingIntervalRef.current = window.setInterval(
      doBatchPoll,
      pollInterval,
    );

    // Return cleanup function
    return () => {
      if (batchPollingIntervalRef.current) {
        log('Cleaning up batch polling interval');
        clearInterval(batchPollingIntervalRef.current);
        batchPollingIntervalRef.current = null;
      }
      setIsBatchPollingActive(false);
      // Also update isAnyUploading in cleanup
      setIsAnyUploading(false);
      log('Batch polling stopped');
    };
  }, [
    executeQuery,
    listingKey,
    maxPollAttempts,
    pollInterval,
    onUploadComplete,
    onUploadError,
    translation,
    isMediaResourceValid,
  ]);

  const addToBatchPolling = useCallback(
    (
      mediaKey: string,
      metadata: MediaMetadata,
      operationType: 'upload' | 'replace' = 'upload',
    ) => {
      const log = loggerRef.current?.log || console.log;

      // Update the ref directly
      pendingMediaPollsRef.current[metadata.name] = {
        mediaKey,
        metadata,
        startTime: Date.now(),
      };

      currentOperationTypeRef.current = operationType;

      log(
        `Added ${metadata.name} to batch polling queue (mediaKey: ${mediaKey}, operation: ${operationType})`,
      );

      // Only start if not already running
      if (!isBatchPollingActive && !batchPollingIntervalRef.current) {
        log('Starting batch polling process');
        startBatchPolling();
      } else {
        log(
          `Batch polling already active with ${Object.keys(pendingMediaPollsRef.current).length} items, skipping restart`,
        );
      }
    },
    [isBatchPollingActive, startBatchPolling],
  );

  /**
   * Legacy pollMediaStatus function - used only for virtual tours now
   */
  const pollMediaStatus = useCallback(
    async (
      mediaKey: string,
      _metadata: MediaMetadata,
    ): Promise<MediaUploadResult | null> => {
      try {
        const result = await executeQuery<any>(`CWA_Media('${mediaKey}')`);

        if (result) {
          // For virtual tours, no processing needed
          return translateMediaRecord(result, translation);
        }

        return null;
      } catch (error) {
        console.error('Error polling media status:', error);
        return null;
      }
    },
    [executeQuery, translation],
  );

  const uploadMedia = useCallback(
    async (metadata: MediaMetadata): Promise<void> => {
      try {
        // Start with 'preparing' stage at 5%
        setUploadProgress((prev) => {
          const newState = {
            ...prev,
            currentFile: {
              name: metadata.name,
              previewUrl: '',
              stage: 'preparing',
              progress: 5,
              statusText: 'Preparing upload...',
            },
          };

          // Start progress simulation for preparing stage (5% to 15%)
          startProgressSimulation(metadata.name, 'preparing', 5, 15, 2000);

          return newState;
        });

        // Get base64 data (this can take time for large files)
        setUploadProgress((prev) => {
          // Update to 'reading' stage at 15%
          const newState = {
            ...prev,
            currentFile: {
              ...prev.currentFile,
              stage: 'reading',
              progress: 15,
              statusText: 'Reading file...',
            },
          };

          // Start progress simulation for reading stage (15% to 25%)
          startProgressSimulation(metadata.name, 'reading', 15, 25, 3000);

          return newState;
        });

        const base64Data = metadata.file
          ? await getBase64Data(metadata.file)
          : undefined;

        // Once we have base64 data, show the preview and update progress
        const previewUrl = base64Data
          ? `data:${metadata.type};base64,${base64Data}`
          : '';

        setActiveUploads((prev) => new Set(prev).add(metadata.name));
        setIsAnyUploading(true);
        setUploadStates((prev) => ({
          ...prev,
          [metadata.name]: { isUploading: true, progress: 30, previewUrl },
        }));

        // Update to 'uploading' stage at 30%
        setUploadProgress((prev) => {
          const newState = {
            ...prev,
            currentFile: {
              name: metadata.name,
              previewUrl,
              stage: 'uploading',
              progress: 30,
              statusText: 'Creating media record...',
            },
          };

          // Start progress simulation for uploading stage (30% to 40%)
          startProgressSimulation(metadata.name, 'uploading', 30, 40, 3000);

          return newState;
        });

        const cleanMediaType = metadata.type.includes('/')
          ? metadata.type.split('/')[1]
          : metadata.type;

        const mediaRecordPayload = {
          CWAPropertyType: listingDetails.CWAPropertyType,
          CWAMEDVWLISKey: listingDetails.CWALISKey,
          CWAResourceRecordKey: listingKey,
          CWAPropMediaInformationValidFlag: true,
          CWACounty: listingDetails.CWACounty,
          CWAMediaSourceFileName: metadata.file?.name || null,
          CWAMediaCategory: metadata.category,
          CWAMediaBytes: 0,
          CWAMediaVisibility: metadata.visibility,
          CWAMediaItemNumber: metadata.itemNumber,
          CWAMediaDisplayOrder: metadata.displayOrder,
          CWAMediaShortDescription: metadata.shortDescription || '',
          CWAMediaLongDescription:
            metadata.description === null ? null : metadata.description || '',
          CWAMediaSizeDescription: metadata.sizeDescription || 'BRIGHT_HD',
          CWAMediaSubSystemLocale: 'BRIGHT_MAIN',
          CWAMediaSystemLocale: 'BRIGHT',
          CWAMediaType: cleanMediaType,
          CWAPreferredPhotoYN: null,
          CWAMediaSourceBusinessPartner: 'BRIGHT',
        };

        if (metadata.type === 'txt') {
          // When the type of upload is TXT we must provide the file name for RESO
          (mediaRecordPayload as any).CWAMediaFileName = metadata.name;
        }

        // Create media record with timeout and retry logic
        const mediaRecord = await retryWithExponentialBackoff(
          async () => {
            return await createMediaRecord(mediaRecordPayload);
          },
          3, // maxRetries
          1000, // initialDelay (1 second)
          2, // backoffFactor (doubles each time: 1s, 2s, 4s)
          loggerRef.current, // pass the logger
        );

        if (mediaRecord.CWAMediaKey === null) {
          console.error(
            `MEDIA_KEY is null on response from POST to CWA_Media: ${JSON.stringify(mediaRecord)}`,
          );
          throw new Error(
            `MEDIA_KEY is null on response from POST to CWA_Media: ${JSON.stringify(mediaRecord)}`,
          );
        }

        // Update to 'sending' stage at 40%
        setUploadProgress((prev) => {
          const newState = {
            ...prev,
            currentFile: {
              ...prev.currentFile,
              stage: 'sending',
              progress: 40,
              statusText: 'Uploading content...',
            },
          };

          // Start progress simulation for sending stage (40% to 60%)
          startProgressSimulation(metadata.name, 'sending', 40, 60, 5000);

          return newState;
        });

        // Process image and upload content
        if (base64Data && mediaRecord.CWAMEDVWKey) {
          let processedBase64Data = base64Data;
          let blob: Blob;

          // Only process images
          if (metadata.type.startsWith('image/')) {
            try {
              // 1. Get the exif data from the image
              const resizeImgConfig = {
                file: metadata.file!,
                targetSize: {
                  width: ImageUtils.TARGET_WIDTH,
                  height: ImageUtils.TARGET_HEIGHT,
                },
              };
              const exifResponse = await getExifInfo(resizeImgConfig);
              // 2. Downscale the image if necessary, and put the exif data in the resized image.
              const result = await downscaleImage(
                base64Data,
                imageConfig,
                exifResponse,
              );
              processedBase64Data = result.base64Data;

              blob = dataURItoBlob(processedBase64Data);
            } catch (error) {
              console.warn(
                'Image processing failed, using original image:',
                error,
              );
              blob = dataURItoBlob(processedBase64Data);
            }
          } else {
            const binaryString = atob(processedBase64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }

            blob = new Blob([bytes], { type: metadata.type });
          }

          // Upload the content - THIS IS THE KEY POINT AFTER WHICH WE START BATCH POLLING
          const log = loggerRef.current?.log || console.log;
          log(
            `Uploading content for ${metadata.name} (mediaKey: ${mediaRecord.CWAMEDVWKey})`,
          );

          try {
            await retryWithExponentialBackoff(
              async () => {
                return await uploadMediaContent(
                  mediaRecord.CWAMEDVWKey,
                  blob,
                  metadata.type,
                );
              },
              3, // maxRetries
              1000, // initialDelay (1 second)
              2, // backoffFactor (doubles each time: 1s, 2s, 4s)
              loggerRef.current, // pass the logger
            );

            log(
              `Content upload complete for ${metadata.name}, now moving to processing stage`,
            );
          } catch (uploadError) {
            log(
              `Failed to upload content for ${metadata.name} after all retry attempts`,
            );
            throw uploadError; // Re-throw to be handled by the existing error handling
          }

          // Update to processing stage at 60%
          setUploadProgress((prev) => {
            const newState = {
              ...prev,
              currentFile: {
                ...prev.currentFile,
                stage: 'processing',
                progress: 60,
                statusText: 'Processing image...',
              },
            };

            // Start progress simulation for processing stage (60% to 90%)
            startProgressSimulation(
              metadata.name,
              'processing',
              60,
              90,
              maxPollAttempts * pollInterval * 0.8,
            );

            return newState;
          });

          // Add to batch polling for everything except virtual tours
          addToBatchPolling(mediaRecord.CWAMEDVWKey, metadata, 'upload');
        } // For virtual tours, we don't need to wait for processing
        else if (metadata.category === 'Unbranded Virtual Tour') {
          const result = await pollMediaStatus(
            mediaRecord.CWAMEDVWKey,
            metadata,
          );
          if (result) {
            // Clear any active progress simulation
            if (progressIntervals.current[metadata.name]) {
              clearInterval(progressIntervals.current[metadata.name]);
              delete progressIntervals.current[metadata.name];
            }
            setUploadProgress((prev) => ({
              ...prev,
              completedFiles: prev.completedFiles + 1,
              currentFile: {
                ...prev.currentFile,
                stage: 'complete',
                progress: 100,
                statusText: 'Upload complete!',
              },
              // Move to next file if available
              ...(prev.remainingFiles.length > 0
                ? {
                    currentFile: {
                      name: prev.remainingFiles[0],
                      previewUrl: '',
                      stage: 'preparing',
                      progress: 0,
                      statusText: 'Preparing...',
                    },
                    remainingFiles: prev.remainingFiles.slice(1),
                  }
                : {}),
            }));

            setUploadStates((prev) => ({
              ...prev,
              [metadata.name]: {
                previewUrl: prev[metadata.name]?.previewUrl || '',
                isUploading: false,
                progress: 100,
              },
            }));

            setActiveUploads((prev) => {
              const next = new Set(prev);
              next.delete(metadata.name);
              if (next.size === 0) {
                setIsAnyUploading(false);
              }
              return next;
            });

            onUploadComplete?.(result);
          }
          return;
        }
      } catch (error) {
        // Clear any active progress simulation
        if (progressIntervals.current[metadata.name]) {
          clearInterval(progressIntervals.current[metadata.name]);
          delete progressIntervals.current[metadata.name];
        }

        // If this is a MimeType error, skip showing upload error UI
        // (MediaSyncDialog will be shown instead)
        if (isMimeTypeNullError(error)) {
          setActiveUploads((prev) => {
            const next = new Set(prev);
            next.delete(metadata.name);
            if (next.size === 0) {
              setIsAnyUploading(false);
            }
            return next;
          });
          return; // Exit early, don't show error UI
        }

        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';

        // Handle upload error
        setUploadProgress((prev) => ({
          ...prev,
          currentFile: {
            ...prev.currentFile,
            stage: 'error',
            error: errorMessage,
            statusText: 'Upload failed',
          },
          failedFiles: [
            ...prev.failedFiles,
            {
              name: metadata.name,
              error: errorMessage,
            },
          ],
        }));

        setUploadStates((prev) => ({
          ...prev,
          [metadata.name]: {
            isUploading: false,
            progress: 0,
            error: error as Error,
            previewUrl: prev[metadata.name]?.previewUrl || '',
          },
        }));

        setActiveUploads((prev) => {
          const next = new Set(prev);
          next.delete(metadata.name);
          if (next.size === 0) {
            setIsAnyUploading(false);
          }
          return next;
        });

        onUploadError?.(error as Error, metadata);
      }
    },
    [
      createMediaRecord,
      uploadMediaContent,
      listingDetails.CWAPropertyType,
      listingDetails.CWALISKey,
      listingDetails.CWACounty,
      listingKey,
      pollMediaStatus,
      onUploadComplete,
      onUploadError,
      getBase64Data,
      imageConfig,
      addToBatchPolling,
      maxPollAttempts,
      pollInterval,
    ],
  );

  const replaceMedia = useCallback(
    async (imageData: ProcessedImageData): Promise<void> => {
      try {
        const fileName = imageData.mediaRecord.MEDIA_SOURCE_FILE_NAME;
        const log = loggerRef.current?.log || console.log;

        // Start with 'preparing' stage at 10%
        setUploadProgress((prev) => {
          const newState = {
            ...prev,
            currentFile: {
              name: fileName,
              previewUrl: '',
              stage: 'preparing' as UploadStage,
              progress: 10,
              statusText: 'Preparing replacement...',
            },
          };

          // Start progress simulation for preparing stage (10% to 20%)
          startProgressSimulation(fileName, 'preparing', 10, 20, 2000);

          return newState;
        });

        setActiveUploads((prev) => new Set(prev).add(fileName));
        setIsAnyUploading(true);
        setUploadStates((prev) => ({
          ...prev,
          [fileName]: {
            isUploading: true,
            progress: 30,
            previewUrl: imageData.dataURL as string,
          },
        }));

        // Update to 'sending' stage at 20%
        setUploadProgress((prev) => {
          const newState = {
            ...prev,
            currentFile: {
              name: fileName,
              previewUrl: '',
              stage: 'sending' as UploadStage,
              progress: 20,
              statusText: 'Replacing content...',
            },
          };

          // Start progress simulation for sending stage (20% to 60%)
          startProgressSimulation(fileName, 'sending', 20, 60, 5000);

          return newState;
        });

        // Replace content via /$value endpoint (existing pattern)
        try {
          await retryWithExponentialBackoff(
            async () => {
              return await replaceMediaContent(
                imageData.mediaRecord.MED_VW_KEY,
                imageData.blob,
                imageData.file?.type,
              );
            },
            3, // maxRetries
            1000, // initialDelay (1 second)
            2, // backoffFactor (doubles each time: 1s, 2s, 4s)
            loggerRef.current, // pass the logger
          );

          log(`Content replacement complete for ${fileName}`);
        } catch (replaceError) {
          log(
            `Failed to replace content for ${fileName} after all retry attempts`,
          );
          throw replaceError;
        }

        // For photos, add to batch polling for processing status
        if (
          imageData.file?.type?.startsWith('image/') ||
          imageData.blob?.type.startsWith('image/')
        ) {
          // Update to processing stage at 60%
          setUploadProgress((prev) => {
            const newState = {
              ...prev,
              currentFile: {
                ...prev.currentFile,
                stage: 'processing' as UploadStage,
                progress: 60,
                statusText: 'Processing replacement...',
              },
            };

            // Start progress simulation for processing stage (60% to 90%)
            startProgressSimulation(
              fileName,
              'processing',
              60,
              90,
              maxPollAttempts * pollInterval * 0.8,
            );

            return newState;
          });

          // Add to batch polling with minimal metadata
          addToBatchPolling(
            imageData.mediaRecord.MED_VW_KEY,
            {
              category: 'Photo',
              description: imageData.mediaRecord.MEDIA_LONG_DESCRIPTION,
              displayOrder: imageData.mediaRecord.MEDIA_DISPLAY_ORDER,
              name: imageData.mediaRecord.MEDIA_SOURCE_FILE_NAME,
              type: imageData.file?.type as string,
              itemNumber: imageData.mediaRecord.MEDIA_ITEM_NUMBER,
              size: imageData.blob?.size as number,
              file: imageData.file as File,
            },
            'replace',
          );
        } else {
          // Non-image media - complete immediately
          if (progressIntervals.current[fileName]) {
            clearInterval(progressIntervals.current[fileName]);
            delete progressIntervals.current[fileName];
          }

          setUploadProgress((prev) => ({
            ...prev,
            completedFiles: prev.completedFiles + 1,
            currentFile: {
              ...prev.currentFile,
              stage: 'complete' as UploadStage,
              progress: 100,
              statusText: 'Replacement complete!',
            },
            // Move to next file if available
            ...(prev.remainingFiles.length > 0
              ? {
                  currentFile: {
                    name: prev.remainingFiles[0],
                    previewUrl: '',
                    stage: 'preparing' as UploadStage,
                    progress: 0,
                    statusText: 'Preparing...',
                  },
                  remainingFiles: prev.remainingFiles.slice(1),
                }
              : {}),
          }));

          setUploadStates((prev) => ({
            ...prev,
            [fileName]: {
              isUploading: false,
              progress: 100,
              previewUrl: prev[fileName]?.previewUrl || '',
            },
          }));

          setActiveUploads((prev) => {
            const next = new Set(prev);
            next.delete(fileName);
            if (next.size === 0) {
              setIsAnyUploading(false);
            }
            return next;
          });

          // Call completion handler if provided
          onReplaceComplete?.(imageData.mediaRecord);
        }
      } catch (error) {
        // Clear any active progress simulation
        const fileName = imageData.mediaRecord.MEDIA_SOURCE_FILE_NAME;
        if (progressIntervals.current[fileName]) {
          clearInterval(progressIntervals.current[fileName]);
          delete progressIntervals.current[fileName];
        }

        // If this is a MimeType error, skip showing replacement error UI
        // (MediaSyncDialog will be shown instead)
        if (isMimeTypeNullError(error)) {
          setActiveUploads((prev) => {
            const next = new Set(prev);
            next.delete(fileName);
            if (next.size === 0) {
              setIsAnyUploading(false);
            }
            return next;
          });
          return; // Exit early, don't show error UI
        }

        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';

        // Handle replacement error
        setUploadProgress((prev) => ({
          ...prev,
          currentFile: {
            ...prev.currentFile,
            stage: 'error' as UploadStage,
            error: errorMessage,
            statusText: 'Replacement failed',
          },
          failedFiles: [
            ...prev.failedFiles,
            {
              name: fileName,
              error: errorMessage,
            },
          ],
        }));

        setUploadStates((prev) => ({
          ...prev,
          [fileName]: {
            isUploading: false,
            progress: 0,
            error: error as Error,
            previewUrl: prev[fileName]?.previewUrl || '',
          },
        }));

        setActiveUploads((prev) => {
          const next = new Set(prev);
          next.delete(fileName);
          if (next.size === 0) {
            setIsAnyUploading(false);
          }
          return next;
        });

        onReplaceError?.(error as Error, imageData);
      }
    },
    [
      replaceMediaContent,
      addToBatchPolling,
      onReplaceComplete,
      onReplaceError,
      maxPollAttempts,
      pollInterval,
      startProgressSimulation,
    ],
  );

  const uploadMediaBatch = useCallback(
    async (mediaItems: MediaMetadata[]) => {
      const log = loggerRef.current?.log || console.log;

      // Clear any existing intervals and pending media
      clearAllIntervals();

      // Reset the pending media polls ref (not just the state)
      pendingMediaPollsRef.current = {};
      setPendingMediaPolls({});

      // Reset shadow failure counts for the new batch
      shadowFailureCountsRef.current = {};

      // Set operation type for this batch
      currentOperationTypeRef.current = 'upload';

      if (mediaItems.length === 0) {
        return {
          results: [],
          totalCount: 0,
          successCount: 0,
          failureCount: 0,
        };
      }

      setUploadStates((prev) => ({
        ...prev,
        [mediaItems[0]?.name]: {
          previewUrl: '',
          isUploading: true,
          progress: 0,
        },
      }));

      setIsAnyUploading(true);

      startProgressSimulation(mediaItems[0]?.name, 'waiting', 0, 5, 2000);

      // Wait for any active request operations to complete before starting batch upload
      await waitForRequestCompletion('starting batch upload', mediaItems);

      // Initialize progress with all files
      setUploadProgress({
        totalFiles: mediaItems.length,
        completedFiles: 0,
        currentFile: {
          name: mediaItems[0]?.name || '',
          previewUrl: '',
          stage: 'preparing',
          progress: 0,
          statusText: 'Preparing...',
        },
        remainingFiles: mediaItems.slice(1).map((item) => item.name),
        failedFiles: [],
      });

      // Use Promise.allSettled to let all uploads complete regardless of individual failures
      const uploadPromise = Promise.allSettled(mediaItems.map(uploadMedia));

      // Register this upload operation
      registerRequestOperation(uploadPromise);

      const results = await uploadPromise;

      // Process results after all uploads have settled
      const uploadResults = results.map((result, index) => {
        const item = mediaItems[index];

        // Return the result or error for tracking
        return {
          file: item,
          success: result.status === 'fulfilled',
          error: result.status === 'rejected' ? result.reason : undefined,
          result: result.status === 'fulfilled' ? result.value : undefined,
        };
      });

      // Check the ref not the state
      // Only stop batch polling if there are truly no pending items in the ref
      const pendingCount = Object.keys(pendingMediaPollsRef.current).length;
      if (pendingCount === 0) {
        log('No pending media items after batch upload, stopping polling');
        if (batchPollingIntervalRef.current) {
          clearInterval(batchPollingIntervalRef.current);
          batchPollingIntervalRef.current = null;
        }
        setIsAnyUploading(false);
        setIsBatchPollingActive(false);
      } else {
        log(
          `Upload complete but ${pendingCount} items still pending processing`,
        );
      }

      // Return results so component can handle successes and failures
      return {
        results: uploadResults,
        totalCount: mediaItems.length,
        successCount: uploadResults.filter((r) => r.success).length,
        failureCount: uploadResults.filter((r) => !r.success).length,
      };
    },
    [uploadMedia, clearAllIntervals],
  );

  const replaceMediaBatch = useCallback(
    async (items: BatchProcessedImageData): Promise<BatchReplaceResult> => {
      const log = loggerRef.current?.log || console.log;

      // Clear any existing intervals and pending media
      clearAllIntervals();

      // Reset the pending media polls ref (not just the state)
      pendingMediaPollsRef.current = {};
      setPendingMediaPolls({});

      // Reset shadow failure counts for the new batch
      shadowFailureCountsRef.current = {};

      // Set operation type for this batch
      currentOperationTypeRef.current = 'replace';

      if (items.totalProcessed === 0) {
        return {
          results: [],
          totalCount: 0,
          successCount: 0,
          failureCount: 0,
        };
      }

      setUploadStates((prev) => ({
        ...prev,
        [items.processedImages[0]?.mediaRecord.MEDIA_SOURCE_FILE_NAME]: {
          previewUrl: '',
          isUploading: true,
          progress: 0,
        },
      }));

      setIsAnyUploading(true);

      startProgressSimulation(
        items.processedImages[0]?.mediaRecord.MEDIA_SOURCE_FILE_NAME,
        'waiting',
        0,
        5,
        2000,
      );

      // Wait for any active request operations to complete before starting batch replacement
      await waitForRequestCompletion(
        'starting batch replacement',
        items.processedImages,
      );

      // Initialize progress with all files
      setUploadProgress({
        totalFiles: items.totalProcessed,
        completedFiles: 0,
        currentFile: {
          name:
            items.processedImages[0]?.mediaRecord.MEDIA_SOURCE_FILE_NAME ||
            `media-${items.processedImages[0]?.mediaRecord.MEDIA_KEY}` ||
            '',
          previewUrl: '',
          stage: 'preparing',
          progress: 0,
          statusText: 'Preparing replacements...',
        },
        remainingFiles: items.processedImages
          .slice(1)
          .map(
            (item) =>
              item.mediaRecord.MEDIA_SOURCE_FILE_NAME ||
              `media-${item.mediaRecord.MEDIA_KEY}`,
          ),
        failedFiles: [],
      });

      // Use Promise.allSettled to let all replacements complete regardless of individual failures
      const replacePromise = Promise.allSettled(
        items.processedImages.map(replaceMedia),
      );

      // Register this replacement operation
      registerRequestOperation(replacePromise);

      const results = await replacePromise;

      // Process results after all replacements have settled
      const replaceResults = results.map((result, index) => {
        const item = items.processedImages[index];

        // Return the result or error for tracking
        return {
          mediaKey: item.mediaRecord.MEDIA_KEY,
          success: result.status === 'fulfilled',
          error: result.status === 'rejected' ? result.reason : undefined,
          result: result.status === 'fulfilled' ? result.value : undefined,
        };
      });

      // Check the ref not the state
      // Only stop batch polling if there are truly no pending items in the ref
      const pendingCount = Object.keys(pendingMediaPollsRef.current).length;
      if (pendingCount === 0) {
        log('No pending media items after batch replacement, stopping polling');
        if (batchPollingIntervalRef.current) {
          clearInterval(batchPollingIntervalRef.current);
          batchPollingIntervalRef.current = null;
        }
        setIsAnyUploading(false);
        setIsBatchPollingActive(false);
      } else {
        log(
          `Replacement complete but ${pendingCount} items still pending processing`,
        );
      }

      // Return results so component can handle successes and failures
      return {
        results: replaceResults,
        totalCount: items.totalProcessed,
        successCount: replaceResults.filter((r) => r.success).length,
        failureCount: replaceResults.filter((r) => !r.success).length,
      };
    },
    [replaceMedia, clearAllIntervals],
  );

  // Store logger in ref if provided
  useEffect(() => {
    if (logger) {
      loggerRef.current = logger;
    }
  }, [logger]);

  // Clean up all intervals on unmount
  useEffect(() => {
    return () => {
      clearAllIntervals();
    };
  }, []);

  /**
   * Start showing the upload dialog with a processing state for cloud file downloads
   */
  const startProcessingFiles = useCallback((fileCount: number) => {
    setIsAnyUploading(true);
    setUploadProgress({
      totalFiles: fileCount,
      completedFiles: 0,
      currentFile: {
        name: `${fileCount} ${fileCount === 1 ? 'file' : 'files'}`,
        previewUrl: '',
        stage: 'preparing',
        progress: 10,
        statusText: 'Processing file content...',
      },
      remainingFiles: [],
      failedFiles: [],
    });
  }, []);

  return {
    uploadMedia,
    uploadMediaBatch,
    replaceMedia,
    replaceMediaBatch,
    uploadStates,
    isUploading: isAnyUploading,
    uploadProgress,
    isMediaResourceValid,
    isBatchPollingActive,
    startProcessingFiles,
  };
}
